import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import { Box } from "@mui/material";
import FormFieldOverlayLayer from "../overlays/FormFieldOverlayLayer";
import { buildPreviewPageViewport } from "./previewPageViewport";

const PreviewPDF = ({
  base64File,
  pdfInput,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  displayRotation,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  baseViewPort,
  formFields,
  locateFields,
  onLocateWidget,
  hoveredFields,
}) => {
  const previewContainerRef = useRef(null);
  const previewDocumentRef = useRef(null);
  const previewPageRef = useRef(null);
  const [renderedViewport, setRenderedViewport] = useState(null);
  const viewportKey = useMemo(
    () => `${pageNumber}:${displayRotation ?? "native"}`,
    [displayRotation, pageNumber],
  );

  const updateRenderedViewport = useCallback(
    (page) => {
      const viewport = buildPreviewPageViewport(page, displayRotation, 1);
      setRenderedViewport(viewport ? { key: viewportKey, viewport } : null);
    },
    [displayRotation, viewportKey],
  );

  useEffect(() => {
    const loadedPage = previewPageRef.current;

    if (loadedPage?.pageNumber === pageNumber) {
      updateRenderedViewport(loadedPage.page);
    }
  }, [displayRotation, pageNumber, updateRenderedViewport]);

  const overlayViewport =
    renderedViewport?.key === viewportKey
      ? renderedViewport.viewport
      : baseViewPort;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        minWidth: "100%",
        width: "max-content",
      }}
    >
      <Box
        ref={previewContainerRef}
        sx={{
          position: "relative",
          display: "inline-block",
          border: "2px solid #d3d3d3",
          borderRadius: "18px",
          boxShadow: "3px 4px 12px rgba(0,0,0,0.3)",
          backgroundColor: "#fff",
          userSelect: "none",
          marginBottom: "6px",
        }}
      >
        <Document
          file={base64File || pdfInput}
          onLoadSuccess={onDocumentLoadSuccess}
          ref={previewDocumentRef}
        >
          <Page
            pageNumber={pageNumber}
            scale={1}
            rotate={displayRotation}
            renderTextLayer={false} // hide selectable text layer in preview
            renderAnnotationLayer={false}
            onLoadSuccess={(page) => {
              previewPageRef.current = { page, pageNumber };
              updateRenderedViewport(page);
              onPageLoadSuccess(page);
            }}
          />
        </Document>

        {/* Render fields as read-only overlays */}
        <FormFieldOverlayLayer
          currentProcessingFile={currentProcessingFile}
          pageNumber={pageNumber}
          scale={1}
          selectToolActive={locateFields}
          typingToolActive={false}
          highlightFields={false} // no yellow highlight
          textOverlayDisplayMode="none"
          focusedField={null}
          setFocusedField={() => {}}
          currCursor="default"
          unassignedFieldNames={[]}
          fixedInputs={[]}
          inputRef={null}
          childInputRef={null}
          pdfContainerRef={previewContainerRef}
          setCurrentProcessingFile={setCurrentProcessingFile}
          // All interaction handlers are no-ops in preview
          setStartPoint={() => {}}
          setRect={() => {}}
          setIsDrawing={() => {}}
          setCurrCursor={() => {}}
          setFreeSelectStartPoint={() => {}}
          setMoving={() => {}}
          setIsSelecting={() => {}}
          setInitialMousePosition={() => {}}
          setInitialInputSize={() => {}}
          setInputPosition={() => {}}
          setSelectionRect={() => {}}
          setResizing={() => {}}
          setResizeDirection={() => {}}
          handleMouseDown={() => {}}
          handleFieldChange={() => {}}
          undoRedoStats={null}
          activeEditorTool={locateFields ? "edit" : "hand"}
          isPreview={true}
          baseViewPort={overlayViewport}
          fieldList={formFields}
          onPreviewFieldClick={locateFields ? onLocateWidget : undefined}
          previewHoveredFieldNames={hoveredFields?.map((field) => field?.name)}
        />
      </Box>
    </Box>
  );
};

export default PreviewPDF;
